import React from 'react';
import { eventNameBuilder } from '@zr/common/js/generate_event_names';
import alertBubble from '@zr/common/js/alertBubble';
import apiRequest from '@zr/utilities/api';
import { Button } from '@zr/zds/src/components/Button';
import { AppliedStatus } from './AppliedStatusLabel';
import { initializeApplyFlow } from '../../../react/components/ApplyFlow/ApplyFlowEntry';

export type ApplyButtonProps = {
  /**
   * Job EncryptedQuizID
   */
  encryptedQuizId: string;

  /**
   * Job LVK
   */
  listingVersionKey: string;

  /**
   * Placement ID of the page
   */
  placementId: string;

  /**
   * Job AppliedStatus
   * This can be mapped from the number values found in mixer:
   * 0 = in_progress
   * 1 = delivered
   * 2 = not_started
   */
  appliedStatus: AppliedStatus;

  /**
   * The click-wrapped job link URL
   * Used for tracking intent to apply
   */
  intentToApplyUrl?: string;

  /**
   * Title of the job being applied to
   * Used for accessibility labeling
   */
  jobTitle?: string;

  /**
   * Whether the user is 1-click apply eligible
   * @defaultValue false
   */
  isOneClickApply?: boolean;

  /**
   * Whether the job is external
   * External jobs are just simple links and will not trigger the Apply Flow
   * If this is true, `jobUrl` should be supplied as well
   * @defaultValue false
   */
  isExternal?: boolean;

  /**
   * The job URL to link to for external apply
   */
  jobApplyUrl?: string;

  /**
   * The job page URL to link to for "View Job Details"
   */
  jobPageUrl?: string;

  /**
   * Defines the target behavior of external job links
   * If this is true, `isExternal` should also be true
   * @defaultValue false
   */
  openInTab?: boolean;

  /**
   * Callback that fires when an external apply button is clicked
   */
  onExternalApply?: () => void;
};

const sendIntentToApply = (url: string) => {
  if (!url) {
    // eslint-disable-next-line no-console
    console.warn("No intent to apply url, can't post click tracker");
    return;
  }

  const headers = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };
  apiRequest(url, { method: 'POST', headers }, '', true).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('Error sending request to Intent to Apply URL', err);
  });
};

export const ApplyButton = ({
  encryptedQuizId,
  listingVersionKey,
  placementId,
  appliedStatus,
  intentToApplyUrl,
  jobTitle,
  jobApplyUrl,
  jobPageUrl,
  isOneClickApply = false,
  isExternal = false,
  openInTab = false,
  onExternalApply,
}: ApplyButtonProps): JSX.Element | null => {
  let initialApplyState = 'default';
  if (appliedStatus === 'in_progress') {
    initialApplyState = 'interview';
  }
  if (appliedStatus === 'delivered') {
    initialApplyState = 'applied';
  }
  const [applyState, setApplyState] = React.useState(initialApplyState);

  const onDone = (applicationState: string) => {
    switch (applicationState) {
      case 'not_started':
        setApplyState('default');
        break;
      case 'in_progress':
        setApplyState('interview');
        break;
      case 'delivered':
        setApplyState('applied');
        alertBubble('Your application was submitted!', 3000);
        break;
      default:
    }
  };

  const handleApply = (): void => {
    if (intentToApplyUrl) {
      sendIntentToApply(intentToApplyUrl);
    }
    setApplyState('applying');
    initializeApplyFlow({
      applyParameters: {
        encryptedQuizId,
        listingVersionKey,
        placementId,
        onDone,
        // TODO figure out how this works and parameterize things appropriately
        ...eventNameBuilder({ template: 'job-results', page_section: '', description: 'one_click_apply' }),
      },
    });
  };

  const useApplyFlow = encryptedQuizId !== '';
  if (useApplyFlow) {
    if (applyState === 'interview') {
      return (
        <Button
          variant="secondary"
          grow
          onClick={handleApply}
          fullWidth
          ariaLabel={jobTitle && `Answer Questions for ${jobTitle}`}
        >
          Finish Screening Questions
        </Button>
      );
    }
    if (applyState === 'delivered') {
      <Button variant="ghost" grow disabled fullWidth ariaLabel={jobTitle && `Application Submitted to ${jobTitle}`}>
        Applied
      </Button>;
    }
    if (applyState === 'applying') {
      <Button
        variant="secondary"
        grow
        onClick={handleApply}
        fullWidth
        ariaLabel={jobTitle && `Application in Progress for ${jobTitle}`}
      >
        Application in Progress
      </Button>;
    }
    if (isOneClickApply) {
      return (
        <Button
          variant="secondary"
          grow
          onClick={handleApply}
          fullWidth
          ariaLabel={jobTitle && `1-Click Apply to ${jobTitle}`}
        >
          1-Click Apply
        </Button>
      );
    }
    return (
      <Button
        variant="secondary"
        grow
        onClick={handleApply}
        fullWidth
        ariaLabel={jobTitle && `Quick Apply to ${jobTitle}`}
      >
        Quick Apply
      </Button>
    );
  }

  if (isExternal) {
    return (
      <Button
        variant="secondary"
        grow
        icon="external"
        iconPos="right"
        ariaLabel={jobTitle && `Apply to ${jobTitle}`}
        href={jobApplyUrl ?? ''}
        target={openInTab ? '_blank' : '_self'}
        rel={openInTab ? 'sponsored noopener' : 'sponsored'}
        onClick={() => {
          onExternalApply?.();
        }}
      >
        Apply
      </Button>
    );
  }

  return (
    <Button variant="secondary" grow ariaLabel={jobTitle && `View Job Details ${jobTitle}`} href={jobPageUrl ?? ''}>
      View Job Details
    </Button>
  );
};
